/**
 * Service for handling tutor-related API calls
 */
import { useAuthStore } from '@features/auth/store/authStore';
import api from '@shared/services/api';
import { TutorConversation, TutorMessage } from '../types';

export const TutorService = {
  /**
   * Get all conversations for the current user
   */
  async getConversations(): Promise<TutorConversation[]> {
    const response = await api.get('/tutor/conversations');
    return response.data.map((conversation: any) => ({
      ...conversation,
      createdAt: new Date(conversation.createdAt),
      updatedAt: new Date(conversation.updatedAt),
    }));
  },

  /**
   * Create a new conversation
   */
  async createConversation(title?: string): Promise<TutorConversation> {
    // Ensure title is a string or undefined
    const safeTitle = typeof title === 'string' ? title : undefined;

    const response = await api.post('/tutor/conversations', { title: safeTitle });
    return {
      ...response.data,
      createdAt: new Date(response.data.createdAt),
      updatedAt: new Date(response.data.updatedAt),
    };
  },

  /**
   * Get a specific conversation
   */
  async getConversation(conversationId: string): Promise<TutorConversation> {
    const response = await api.get(`/tutor/conversations/${conversationId}`);
    return {
      ...response.data,
      createdAt: new Date(response.data.createdAt),
      updatedAt: new Date(response.data.updatedAt),
    };
  },

  /**
   * Get messages for a conversation
   */
  async getMessages(conversationId: string): Promise<TutorMessage[]> {
    const response = await api.get(`/tutor/conversations/${conversationId}/messages`);
    return response.data.map((message: any) => ({
      ...message,
      createdAt: new Date(message.createdAt),
    }));
  },

  /**
   * Send a message to the tutor and get a streaming response
   * This returns the URL for the EventSource to connect to
   */
  getMessageStreamUrl(conversationId: string, message: string): string {
    // We need to encode the message for the URL
    const encodedMessage = encodeURIComponent(message);
    return `${api.defaults.baseURL}/tutor/conversations/${conversationId}/messages/stream`;
  },

  /**
   * Send a message to the tutor
   * This is used for the actual POST request
   */
  async sendMessage(conversationId: string, message: string): Promise<Response> {
    const token = useAuthStore.getState().token;
    return fetch(`${api.defaults.baseURL}/tutor/conversations/${conversationId}/messages/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify({ message }),
    });
  },

  /**
   * Update conversation title
   */
  async updateConversationTitle(conversationId: string, title: string): Promise<void> {
    await api.patch(`/tutor/conversations/${conversationId}/title`, { title });
  },

  /**
   * Delete a conversation
   */
  async deleteConversation(conversationId: string): Promise<void> {
    await api.delete(`/tutor/conversations/${conversationId}`);
  },
};
